import { useState, useEffect, useMemo } from 'react'
import { Routes, Route, Link, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { documentsApi, todosApi } from '../api/client'
import type { Document, Todo } from '../types'
import DocumentEditor from '../components/editor/DocumentEditor'
import TodoList from '../components/common/TodoList'
import DocumentCard from '../components/common/DocumentCard'
import LanguageSwitcher from '../components/common/LanguageSwitcher'

export default function DashboardPage() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [todos, setTodos] = useState<Todo[]>([])
  const [completedTodos, setCompletedTodos] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'name' | 'words'>('updated')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [docsRes, todosRes] = await Promise.all([
        documentsApi.getAll(),
        todosApi.getAll(),
      ])

      if (docsRes.data.success) {
        setDocuments(docsRes.data.data)
      }

      if (todosRes.data.success) {
        setTodos(todosRes.data.data.todos)
        setCompletedTodos(todosRes.data.data.completed)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTodoToggle = async (id: string, completed: boolean) => {
    try {
      await todosApi.update(id, { completed })
      loadData()
    } catch (error) {
      console.error('Error updating todo:', error)
    }
  }

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(query) ||
        doc.type.toLowerCase().includes(query)
      )
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(doc => doc.type === selectedType)
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(doc => doc.status === selectedStatus)
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'updated':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'name':
          return a.title.localeCompare(b.title)
        case 'words':
          return b.wordCount - a.wordCount
        default:
          return 0
      }
    })

    return sorted
  }, [documents, searchQuery, selectedType, selectedStatus, sortBy])

  // Calculate progress
  const totalTodos = todos.length + completedTodos.length
  const completionRate = totalTodos > 0 ? Math.round((completedTodos.length / totalTodos) * 100) : 0
  
  // Group todos by phase
  const todosByPhase = useMemo(() => {
    const grouped: Record<string, Todo[]> = {}
    todos.forEach(todo => {
      const phase = todo.phase || 'Uncategorized'
      if (!grouped[phase]) {
        grouped[phase] = []
      }
      grouped[phase].push(todo)
    })
    return grouped
  }, [todos])

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-seal-red mx-auto"></div>
          <p className="mt-4 text-ink-black">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="min-h-screen bg-paper-bg">
          {/* Header */}
          <header className="bg-ink-black text-warm-parchment py-6 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="font-serif text-2xl md:text-3xl tracking-wider">
                    The Art of Tourism
                  </h1>
                  <p className="text-sm text-muted-gold mt-1">旅遊之藝</p>
                </div>
                <div className="text-right flex items-center gap-4">
                  <LanguageSwitcher />
                  <div>
                    <p className="text-sm text-mist-gray">Project Dashboard</p>
                    <p className="text-xs text-muted-gold mt-1">
                      {completionRate}% Complete
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Documents Section */}
              <div className="lg:col-span-2">
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="flex items-center justify-between mb-6 pb-2 border-b-2 border-muted-gold">
                    <h2 className="font-serif text-2xl text-ink-black">
                      Documents
                    </h2>
                    <span className="text-sm text-mist-gray">
                      {filteredDocuments.length} of {documents.length}
                    </span>
                  </div>

                  {/* Search and Filters */}
                  <div className="mb-6 space-y-4">
                    {/* Search Bar */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 pl-10 bg-white border border-muted-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-seal-red focus:border-transparent"
                      />
                      <svg
                        className="absolute left-3 top-2.5 h-5 w-5 text-mist-gray"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-3">
                      {/* Type Filter */}
                      <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="px-3 py-1.5 bg-white border border-muted-gold rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-seal-red"
                      >
                        <option value="all">All Types</option>
                        <option value="master">Master</option>
                        <option value="book">Book</option>
                        <option value="strategy">Strategy</option>
                        <option value="research">Research</option>
                        <option value="presentation">Presentation</option>
                        <option value="brand">Brand</option>
                      </select>

                      {/* Status Filter */}
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="px-3 py-1.5 bg-white border border-muted-gold rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-seal-red"
                      >
                        <option value="all">All Status</option>
                        <option value="draft">Draft</option>
                        <option value="review">Review</option>
                        <option value="final">Final</option>
                      </select>

                      {/* Sort */}
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="px-3 py-1.5 bg-white border border-muted-gold rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-seal-red"
                      >
                        <option value="updated">Last Updated</option>
                        <option value="created">Date Created</option>
                        <option value="name">Name</option>
                        <option value="words">Word Count</option>
                      </select>

                      {/* Clear Filters */}
                      {(searchQuery || selectedType !== 'all' || selectedStatus !== 'all') && (
                        <button
                          onClick={() => {
                            setSearchQuery('')
                            setSelectedType('all')
                            setSelectedStatus('all')
                          }}
                          className="px-3 py-1.5 text-sm text-seal-red hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Documents Grid */}
                  <AnimatePresence mode="popLayout">
                    {filteredDocuments.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-12"
                      >
                        <p className="text-mist-gray">No documents found matching your filters.</p>
                      </motion.div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredDocuments.map((doc, index) => (
                          <motion.div
                            key={doc.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                          >
                            <DocumentCard document={doc} />
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </AnimatePresence>
                </motion.section>

                {/* Stats */}
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-8"
                >
                  <h2 className="font-serif text-2xl text-ink-black mb-6 pb-2 border-b-2 border-muted-gold">
                    Project Stats
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard 
                      label="Documents" 
                      value={documents.length} 
                      color="bg-seal-red"
                    />
                    <StatCard 
                      label="Total Words" 
                      value={documents.reduce((acc, doc) => acc + doc.wordCount, 0).toLocaleString()} 
                      color="bg-muted-gold"
                    />
                    <StatCard 
                      label="Pending Tasks" 
                      value={todos.length} 
                      color="bg-mountain-green"
                    />
                    <StatCard 
                      label="Completed" 
                      value={completedTodos.length} 
                      color="bg-ink-black"
                    />
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6 bg-white border border-muted-gold p-4 rounded-sm">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-ink-black font-medium">Project Progress</span>
                      <span className="text-seal-red font-bold">{completionRate}%</span>
                    </div>
                    <div className="w-full h-2 bg-aged-paper rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${completionRate}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="h-full bg-gradient-to-r from-seal-red to-muted-gold"
                      />
                    </div>
                  </div>
                </motion.section>
              </div>

              {/* Sidebar - Todos */}
              <div className="lg:col-span-1">
                <motion.aside
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="bg-white border border-muted-gold rounded-sm p-6 shadow-sm"
                >
                  <h2 className="font-serif text-xl text-ink-black mb-4 pb-2 border-b border-muted-gold">
                    Production Tasks
                  </h2>
                  
                  {/* Grouped by Phase */}
                  {Object.entries(todosByPhase).map(([phase, phaseTodos]) => (
                    <div key={phase} className="mb-4">
                      <h3 className="text-xs font-bold text-mist-gray uppercase tracking-wider mb-2">
                        {phase}
                      </h3>
                      <TodoList 
                        todos={phaseTodos} 
                        onToggle={handleTodoToggle}
                      />
                    </div>
                  ))}
                  
                  {completedTodos.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-muted-gold">
                      <h3 className="font-serif text-lg text-mist-gray mb-3">
                        Completed ({completedTodos.length})
                      </h3>
                      <TodoList 
                        todos={completedTodos.slice(0, 5)} 
                        onToggle={handleTodoToggle}
                        completed
                      />
                    </div>
                  )}
                </motion.aside>

                {/* Quick Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-6 bg-white border border-muted-gold rounded-sm p-6 shadow-sm"
                >
                  <h3 className="font-serif text-lg text-ink-black mb-4 pb-2 border-b border-muted-gold">
                    Quick Links
                  </h3>
                  <ul className="space-y-2">
                    <li>
                      <Link 
                        to="/dashboard/edit/master-document" 
                        className="text-sm text-ink-black hover:text-seal-red transition-colors"
                      >
                        Master Document
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/dashboard/edit/book-outline" 
                        className="text-sm text-ink-black hover:text-seal-red transition-colors"
                      >
                        Book Outline
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/dashboard/edit/speaker-script" 
                        className="text-sm text-ink-black hover:text-seal-red transition-colors"
                      >
                        Speaker Script
                      </Link>
                    </li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </main>
        </div>
      } />
      
      <Route path="/edit/:slug" element={<DocumentEditorWrapper />} />
    </Routes>
  )
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <motion.div 
      whileHover={{ y: -2 }}
      className="bg-white border border-muted-gold p-4 rounded-sm"
    >
      <div className={`w-2 h-2 rounded-full ${color} mb-2`}></div>
      <p className="text-2xl font-serif text-ink-black">{value}</p>
      <p className="text-sm text-mist-gray">{label}</p>
    </motion.div>
  )
}

function DocumentEditorWrapper() {
  const { slug } = useParams<{ slug: string }>()
  const [document, setDocument] = useState<Document | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (slug) {
      loadDocument(slug)
    }
  }, [slug])

  const loadDocument = async (docSlug: string) => {
    try {
      const res = await documentsApi.getBySlug(docSlug)
      if (res.data.success) {
        setDocument(res.data.data)
      }
    } catch (error) {
      console.error('Error loading document:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-seal-red"></div>
      </div>
    )
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-paper-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-ink-black mb-4">Document Not Found</h2>
          <Link to="/dashboard" className="text-seal-red hover:underline">
            Return to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  return <DocumentEditor document={document} />
}